// QuestionBankAdmin.js - Admin tool for generating and managing exam question banks
// Allows admin to generate MC/WR questions per chapter via GPT-4o, view, and delete questions

import React, { useState, useEffect } from 'react';
import { API_BASE_URL, logger, apiFetch } from '../config';
import { getCurriculumUrl } from '../App';

// Helper: render text with proper code formatting as separate lines
const formatCode = (text) => {
  if (!text) return '';
  let result = text;
  // Handle literal two-char \n sequences (from JSON/database)
  result = result.replace(/\\n/g, '\n');
  // Handle semicolons used to join statements
  result = result.replace(/;\s*(?=[a-zA-Z_])/g, '\n');
  // If still no newlines, try to break on Python keywords
  if (!result.includes('\n') && /\b(if |elif |else:|for |while |def |class |print\(|return |import )/.test(result)) {
    result = result
      .replace(/\s+(if\s)/g, '\n$1')
      .replace(/\s+(elif\s)/g, '\n$1')
      .replace(/\s+(else:)/g, '\n$1')
      .replace(/\s+(for\s)/g, '\n$1')
      .replace(/\s+(while\s)/g, '\n$1')
      .replace(/\s+(def\s)/g, '\n$1')
      .replace(/\s+(class\s)/g, '\n$1')
      .replace(/\s+(return\s)/g, '\n    $1')
      .replace(/\s+(print\()/g, '\n    $1');
  }
  return result;
};

// Helper: render formatted text as JSX with line breaks
const renderFormatted = (text) => {
  if (!text) return null;
  const formatted = formatCode(text);
  const lines = formatted.split('\n');
  if (lines.length <= 1) return formatted;
  return lines.map((line, i) => (
    <React.Fragment key={i}>
      {line}
      {i < lines.length - 1 && <br />}
    </React.Fragment>
  ));
};

const QuestionBankAdmin = ({ selectedSection }) => {
  // Multi-section support: build list of all user sections (e.g. ['CIS3260-06', 'CIS3260-08'])
  const university = localStorage.getItem('university') || '';
  const allUserSections = (localStorage.getItem('class_section') || '').split(',').map(s => s.trim()).filter(Boolean);

  // Local section picker: 'all' or a specific section string
  const [qbSection, setQbSection] = useState(() => selectedSection || allUserSections[0] || '');

  // Get class_section strings (with university) for API calls
  // Returns array — single element for specific section, all sections for 'all'
  const getTargetSections = () => {
    if (qbSection === 'all') {
      return allUserSections.map(s => `${s}-${university}`);
    }
    return [`${qbSection}-${university}`];
  };

  // Single class_section for API calls that only need one (view, count display)
  const getClassSection = () => {
    if (qbSection === 'all') return `${allUserSections[0]}-${university}`;
    return `${qbSection}-${university}`;
  };

  // Course structure (chapters)
  const [courseStructure, setCourseStructure] = useState([]);
  const [structureLoading, setStructureLoading] = useState(false);

  // Generator form state
  const [selectedChapter, setSelectedChapter] = useState('');
  const [difficulty, setDifficulty] = useState('medium');
  const [questionType, setQuestionType] = useState('mc'); // 'mc' or 'wr'
  const [questionCount, setQuestionCount] = useState(20);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [generationLog, setGenerationLog] = useState([]);
  const [generationResult, setGenerationResult] = useState(null); // { inserted, duplicatesSkipped }

  // Viewing state
  const [viewMode, setViewMode] = useState(null); // null, 'view-all', 'view-chapter'
  const [viewChapter, setViewChapter] = useState('');
  const [viewType, setViewType] = useState('mc');
  const [viewQuestions, setViewQuestions] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);

  // Bank counts per chapter
  const [bankCounts, setBankCounts] = useState({}); // { "slide01": { mc_count: 50, wr_count: 15 }, ... }
  const [countsLoading, setCountsLoading] = useState(false);

  // Delete state
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { chapter, type } or null
  const [deleting, setDeleting] = useState(false);

  // Delete single question
  const [deletingSingleId, setDeletingSingleId] = useState(null);

  // Edit single question
  const [editingQuestion, setEditingQuestion] = useState(null); // full question object being edited
  const [savingEdit, setSavingEdit] = useState(false);

  // Generate Similar Questions state
  const [seedQuestion, setSeedQuestion] = useState('');
  const [similarChapter, setSimilarChapter] = useState('');
  const [similarDifficulty, setSimilarDifficulty] = useState('medium');
  const [similarType, setSimilarType] = useState('mc');
  const [similarGenerating, setSimilarGenerating] = useState(false);
  const [similarResults, setSimilarResults] = useState([]); // [{...question, _accepted, _skipped}]
  const [similarLog, setSimilarLog] = useState('');

  // Load course structure on mount and when section changes
  useEffect(() => {
    loadCourseStructure();
  }, [qbSection]);

  const loadCourseStructure = async () => {
    setStructureLoading(true);
    try {
      const response = await fetch(getCurriculumUrl('course_structure.json'));
      if (!response.ok) throw new Error('Failed to load course structure');
      const structure = await response.json();
      setCourseStructure(structure);
      setStructureLoading(false);

      // Load counts for all chapters
      loadAllCounts(structure);
    } catch (err) {
      logger.error('Error loading course structure:', err);
      setStructureLoading(false);
    }
  };

  const loadAllCounts = async (structure) => {
    setCountsLoading(true);
    const counts = {};
    const sections = getTargetSections();
    for (const chapter of structure) {
      let totalMc = 0, totalWr = 0;
      for (const sec of sections) {
        try {
          const response = await apiFetch(`${API_BASE_URL}/api/questionBank/count?chapter=${chapter.id}&class_section=${encodeURIComponent(sec)}`);
          if (response.ok) {
            const data = await response.json();
            totalMc += data.mc_count || 0;
            totalWr += data.wr_count || 0;
          }
        } catch { /* ignore */ }
      }
      counts[chapter.id] = { mc_count: totalMc, wr_count: totalWr };
    }
    setBankCounts(counts);
    setCountsLoading(false);
  };

  const addLog = (msg) => {
    setGenerationLog(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`]);
  };

  // ==================== GENERATE QUESTIONS ====================
  const handleGenerate = async () => {
    if (!selectedChapter) return alert('Please select a chapter');
    if (questionCount < 1 || questionCount > 100) return alert('Question count must be 1-100');

    setGenerating(true);
    setGenerationLog([]);
    setGenerationResult(null);

    const chapter = courseStructure.find(c => c.id === selectedChapter);
    if (!chapter) { setGenerating(false); return; }

    try {
      // Step 1: Load curriculum content + build chapter lockdown
      addLog(`Loading curriculum content for ${chapter.title}...`);
      const chapterIndex = courseStructure.findIndex(c => c.id === selectedChapter);

      // Load ONLY the selected chapter's content (main focus)
      let chapterContent = '';
      try {
        const response = await fetch(getCurriculumUrl(`${chapter.id}.txt`));
        if (response.ok) {
          chapterContent = await response.text();
          addLog(`Loaded ${chapterContent.length} characters of chapter content.`);
        } else {
          addLog('Warning: Could not load curriculum file, using topic list only.');
        }
      } catch {
        addLog('Warning: Could not load curriculum file, using topic list only.');
      }

      // Build list of previous chapter titles + topics (concepts student already knows)
      const previousChapters = courseStructure.slice(0, chapterIndex);
      const previousChaptersSummary = previousChapters.map((ch, i) =>
        `- Chapter ${i + 1}: ${ch.title} (${ch.topics.join(', ')})`
      ).join('\n');

      // Build future chapter restrictions — check BOTH titles AND topics
      const futureChapters = courseStructure.slice(chapterIndex + 1);
      const futureChapterDetails = futureChapters.map((ch, i) =>
        `- Chapter ${chapterIndex + 2 + i}: ${ch.title} — Topics: ${ch.topics.join(', ')}`
      ).join('\n');
      const futureText = futureChapters.map(ch =>
        `${ch.title.toLowerCase()} ${ch.topics.map(t => t.toLowerCase()).join(' ')}`
      ).join(' ');

      const listsInFuture = futureText.includes('list');
      const functionsInFuture = futureText.includes('function') || futureText.includes('def ') || futureText.includes('return value');
      const dictionariesInFuture = futureText.includes('dictionar') || futureText.includes('key-value');
      const loopsInFuture = futureText.includes('loop') || futureText.includes('iteration') || futureText.includes('while') || futureText.includes('for loop');
      const classesInFuture = futureText.includes('class') || futureText.includes('oop') || futureText.includes('object-oriented') || futureText.includes('__init__');
      const setsInFuture = futureText.includes('set');
      const tuplesInFuture = futureText.includes('tuple');
      const filesInFuture = futureText.includes('file') && futureText.includes('read');
      const exceptionsInFuture = futureText.includes('exception') || futureText.includes('try') || futureText.includes('except');

      const conceptRestrictions = [
        listsInFuture ? '- Lists are in a FUTURE chapter: NO [] syntax, NO append(), NO list comprehension, NO list operations of any kind' : null,
        functionsInFuture ? '- Functions are in a FUTURE chapter: NO def, NO return, NO function definitions or calls to custom functions' : null,
        dictionariesInFuture ? '- Dictionaries are in a FUTURE chapter: NO {} with key:value pairs, NO dict operations' : null,
        loopsInFuture ? '- Loops are in a FUTURE chapter: NO for, NO while loops, NO iteration of any kind' : null,
        classesInFuture ? '- Classes/OOP are in a FUTURE chapter: NO class definitions, NO __init__, NO self, NO object instantiation' : null,
        setsInFuture ? '- Sets are in a FUTURE chapter: NO set() or set operations' : null,
        tuplesInFuture ? '- Tuples are in a FUTURE chapter: NO tuple syntax' : null,
        filesInFuture ? '- File I/O is in a FUTURE chapter: NO open(), NO file reading/writing' : null,
        exceptionsInFuture ? '- Exceptions are in a FUTURE chapter: NO try/except blocks' : null,
      ].filter(Boolean).join('\n');

      // Build allowed topics list (current chapter + previous)
      const currentTopics = chapter.topics.map(t => `  • ${t}`).join('\n');
      const allowedPreviousTopics = previousChapters.flatMap(ch => ch.topics).map(t => `  • ${t}`).join('\n');

      const chapterLockdown = `
[STRICT CHAPTER LOCKDOWN — MUST FOLLOW]
You are generating questions ONLY for Chapter ${chapterIndex + 1}: "${chapter.title}".

ALLOWED topics (ONLY use these):
Current chapter topics (PRIMARY FOCUS — most questions must test these):
${currentTopics}

${previousChapters.length > 0 ? `Previously covered topics (may use as building blocks ONLY, not as main focus):\n${allowedPreviousTopics}` : 'No previous chapters — this is the first chapter.'}

FORBIDDEN — The student has NOT learned these yet. Do NOT use ANY concept from these future chapters:
${futureChapterDetails || 'None - this is the last chapter'}

${conceptRestrictions ? `HARD RESTRICTIONS (violating these makes the question INVALID):\n${conceptRestrictions}` : ''}
${listsInFuture ? 'When needing collections or iteration, use range() or string characters, NEVER lists.' : ''}

CRITICAL: If a generated question uses ANY concept from a future chapter, it is WRONG and must not be included. Every question must be answerable by a student who has ONLY completed chapters 1 through ${chapterIndex + 1}.
[END CHAPTER LOCKDOWN]`;

      // Step 2: Fetch existing questions to avoid duplicates
      addLog(`Fetching existing ${questionType.toUpperCase()} questions for duplicate check...`);
      let existingQuestions = [];
      try {
        const endpoint = questionType === 'mc' ? 'mcBank' : 'wrBank';
        const dedupSec = qbSection === 'all' ? `${allUserSections[0]}-${university}` : `${qbSection}-${university}`;
        const response = await apiFetch(`${API_BASE_URL}/api/${endpoint}/all?chapter=${selectedChapter}&class_section=${encodeURIComponent(dedupSec)}`);
        if (response.ok) {
          const data = await response.json();
          existingQuestions = data.questions || [];
          addLog(`Found ${existingQuestions.length} existing questions.`);
        }
      } catch {
        addLog('Warning: Could not fetch existing questions for dedup.');
      }

      // Build existing question texts for dedup (lowercase trimmed)
      const existingTexts = new Set(
        existingQuestions.map(q => (q.question_text || q.question || '').toLowerCase().trim())
      );

      // Step 3: Build the LLM prompt
      addLog(`Building prompt for AI generation...`);

      const topicsList = chapter.topics.map((t, i) => `${i + 1}. ${t}`).join('\n');

      // Include some existing questions in prompt to help LLM avoid them
      let existingQuestionsHint = '';
      if (existingQuestions.length > 0) {
        // Randomly sample up to 30 existing questions so the LLM sees a diverse mix
        const shuffled = [...existingQuestions].sort(() => Math.random() - 0.5);
        const sample = shuffled.slice(0, 30).map(q => `- ${q.question_text || q.question}`).join('\n');
        existingQuestionsHint = `\n\nIMPORTANT: The following questions ALREADY EXIST in the bank. Do NOT generate any question that is similar to these:\n${sample}`;
        if (existingQuestions.length > 30) {
          existingQuestionsHint += `\n... and ${existingQuestions.length - 30} more existing questions.`;
        }
      }

      let systemPrompt, userPrompt;

      if (questionType === 'mc') {
        systemPrompt = `You are an expert Python programming instructor creating exam questions for a university course. Generate exactly ${questionCount} unique ${difficulty} multiple-choice questions. Each question must test a different concept or scenario — no two questions should test the same thing in a similar way.

Return a JSON object with a single key "questions" containing an array of exactly ${questionCount} question objects. Example format:
{
  "questions": [
    {
      "question_text": "What will be the output of this code?\n\nx = 10\nif x > 5:\n    print('High')\nelse:\n    print('Low')",
      "option_a": "High",
      "option_b": "Low",
      "option_c": "Error",
      "option_d": "None",
      "correct_answer": "A",
      "explanation": "x is 10 which is greater than 5, so the if branch executes and prints 'High'"
    }
  ]
}

CRITICAL CODE FORMATTING RULE:
When a question or option contains Python code, you MUST format it with proper line breaks using \n and proper indentation using spaces inside the JSON string. For example:
- WRONG: "x = 3; if x > 5: print('A') elif x > 2: print('B')"
- CORRECT: "x = 3\nif x > 5:\n    print('A')\nelif x > 2:\n    print('B')"
NEVER use semicolons to join Python statements. ALWAYS use \n for new lines and spaces for indentation.

Rules:
- You MUST return exactly ${questionCount} questions in the "questions" array
- correct_answer must be exactly "A", "B", "C", or "D"
- Questions must be about the topics listed, based on the chapter content provided
- Difficulty level: ${difficulty}
  - easy: recall/definition questions, what does a single statement do, 1-4 lines of code in the question
  - medium: apply concepts, trace code output, 4-8 lines of code in the question
  - hard: debug code, combine multiple concepts, 8-16 lines of code in the question
  - very hard: complex scenarios, tricky edge cases, multi-step reasoning, more than 16 lines of code in the question
- Do NOT use LaTeX notation
- Each question must be UNIQUE — do not repeat similar patterns${existingQuestionsHint}
${chapterLockdown}`;

        userPrompt = `Generate ${questionCount} unique ${difficulty} multiple-choice questions FOCUSED on Chapter ${chapterIndex + 1}: ${chapter.title}

Topics to test (THIS chapter — main focus):
${topicsList}

${chapterContent ? `Chapter content:\n${chapterContent.substring(0, 12000)}` : ''}

Return as JSON with {"questions": [...]}.`;

      } else {
        // WR type
        systemPrompt = `You are an expert Python programming instructor creating exam questions for a university course. Generate exactly ${questionCount} unique ${difficulty} written-response questions that require students to write code or explain concepts.

Return a JSON object with a single key "questions" containing an array of exactly ${questionCount} question objects. Example format:
{
  "questions": [
    {
      "question_text": "Write a program that asks the user for a number and prints whether it is positive, negative, or zero.",
      "sample_solution": "num = float(input('Enter a number: '))\nif num > 0:\n    print('Positive')\nelif num < 0:\n    print('Negative')\nelse:\n    print('Zero')"
    }
  ]
}

CRITICAL CODE FORMATTING RULE:
All Python code in question_text and sample_solution MUST use \n for line breaks and spaces for indentation inside JSON strings. For example:
- WRONG: "x = 3; if x > 5: print('A') elif x > 2: print('B')"
- CORRECT: "x = 3\nif x > 5:\n    print('A')\nelif x > 2:\n    print('B')"
NEVER use semicolons to join Python statements. ALWAYS use \n for new lines and 4 spaces for indentation.

Rules:
- You MUST return exactly ${questionCount} questions in the "questions" array
- EVERY question MUST have a non-empty "sample_solution" field — this is required, never null or empty
- The sample_solution must be a complete, correct answer with properly formatted code
- Questions must be about the topics listed, based on the chapter content provided
- Difficulty level: ${difficulty}
  - easy: write a simple program with 1-4 lines of code
  - medium: write a program with 4-8 lines of code
  - hard: write a program combining 2-3 concepts with 8-16 lines of code
  - very hard: write a full program with more than 16 lines of code, handle edge cases
- Do NOT use LaTeX notation
- Each question must be UNIQUE — do not repeat similar patterns${existingQuestionsHint}
${chapterLockdown}`;

        userPrompt = `Generate ${questionCount} unique ${difficulty} written-response questions FOCUSED on Chapter ${chapterIndex + 1}: ${chapter.title}

Topics to test (THIS chapter — main focus):
${topicsList}

${chapterContent ? `Chapter content:\n${chapterContent.substring(0, 12000)}` : ''}

Return as JSON with {"questions": [...]}.`;
      }

      // Step 4: Call GPT-4o
      addLog(`Generating ${questionCount} ${difficulty} ${questionType.toUpperCase()} questions...`);

      const response = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4.1',
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: userPrompt }
          ],
          temperature: 0.8,
          max_tokens: 16384,
          response_format: { type: 'json_object' }
        })
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`OpenAI API error: ${response.status} - ${errText}`);
      }

      const data = await response.json();
      let rawContent = data.choices[0].message.content.trim();
      addLog(`Received AI response (${rawContent.length} chars).`);

      // Step 5: Parse JSON
      addLog('Parsing response...');
      // Remove markdown code fences if present
      rawContent = rawContent.replace(/^```json\s*/i, '').replace(/^```\s*/i, '').replace(/\s*```$/i, '');

      let questions;
      try {
        const parsed = JSON.parse(rawContent);
        // response_format json_object may wrap in { "questions": [...] }
        if (Array.isArray(parsed)) {
          questions = parsed;
        } else if (typeof parsed === 'object' && parsed !== null) {
          // Log the keys so we can see what the LLM returned
          const keys = Object.keys(parsed);
          addLog(`Response is an object with keys: ${keys.join(', ')}`);
          
          // Try common key names
          const possibleKeys = ['questions', 'mc_questions', 'wr_questions', 'data', 'items', 'results', 'quiz', 'question_bank'];
          let found = false;
          for (const key of possibleKeys) {
            if (parsed[key] && Array.isArray(parsed[key])) {
              questions = parsed[key];
              addLog(`Found questions array under key: "${key}"`);
              found = true;
              break;
            }
          }
          
          if (!found) {
            // Try to find ANY array value in the object
            for (const key of keys) {
              if (Array.isArray(parsed[key]) && parsed[key].length > 0) {
                questions = parsed[key];
                addLog(`Found questions array under key: "${key}"`);
                found = true;
                break;
              }
            }
          }

          if (!found) {
            // Maybe the object itself is a single question? Wrap it.
            if (parsed.question_text || parsed.question) {
              questions = [parsed];
              addLog('Response was a single question object, wrapped in array.');
            } else {
              throw new Error(`No array found in response. Keys: ${keys.join(', ')}`);
            }
          }
        } else {
          throw new Error('Response is not a JSON object or array');
        }
      } catch (parseErr) {
        addLog(`Parse error: ${parseErr.message}`);
        addLog(`Raw content preview: ${rawContent.substring(0, 300)}...`);
        // Fallback: try to extract JSON array from raw text
        const match = rawContent.match(/\[[\s\S]*\]/);
        if (match) {
          try {
            questions = JSON.parse(match[0]);
          } catch (e2) {
            // Last resort: try fixing common JSON issues
            let fixed = match[0]
              .replace(/(["'])\s*\n\s*/g, '$1, ')
              .replace(/,\s*}/g, '}')
              .replace(/,\s*]/g, ']');
            questions = JSON.parse(fixed);
          }
        } else {
          throw new Error(`Failed to parse LLM response as JSON: ${parseErr.message}`);
        }
      }

      if (!Array.isArray(questions)) {
        throw new Error('Response is not a JSON array');
      }

      addLog(`Parsed ${questions.length} questions from response.`);

      // Step 6: Deduplicate against existing
      const uniqueQuestions = questions.filter(q => {
        const text = (q.question_text || q.question || '').toLowerCase().trim();
        if (existingTexts.has(text)) return false;
        // Also check for near-duplicates within this batch
        if (existingTexts.has(text)) return false;
        existingTexts.add(text); // prevent intra-batch dupes
        return true;
      });

      const duplicatesSkipped = questions.length - uniqueQuestions.length;
      addLog(`Dedup: ${uniqueQuestions.length} unique, ${duplicatesSkipped} duplicates skipped.`);

      if (uniqueQuestions.length === 0) {
        addLog('⚠️ All generated questions were duplicates. Try again or increase count.');
        setGenerating(false);
        setGenerationResult({ inserted: 0, duplicatesSkipped });
        return;
      }

      // Step 6b: For WR — ensure sample_solution is never null/empty
      if (questionType === 'wr') {
        const beforeCount = uniqueQuestions.length;
        for (let i = uniqueQuestions.length - 1; i >= 0; i--) {
          const q = uniqueQuestions[i];
          if (!q.sample_solution || q.sample_solution.trim() === '') {
            // Try alternative keys the LLM might have used
            q.sample_solution = q.sample_answer || q.sampleSolution || q.answer || q.solution || 'No sample solution provided.';
          }
          if (!q.question_text && !q.question) {
            uniqueQuestions.splice(i, 1); // remove questions with no text at all
          }
        }
        if (uniqueQuestions.length < beforeCount) {
          addLog(`Removed ${beforeCount - uniqueQuestions.length} questions with missing text.`);
        }
      }

      // For MC — ensure explanation is never null
      if (questionType === 'mc') {
        for (const q of uniqueQuestions) {
          if (!q.explanation || q.explanation.trim() === '') {
            q.explanation = 'N/A';
          }
        }
      }

      // Step 7: Stamp chapter and difficulty into each question, normalize keys for backend
      // Log first question's keys for debugging
      if (uniqueQuestions.length > 0) {
        addLog(`First question keys: ${Object.keys(uniqueQuestions[0]).join(', ')}`);
        if (questionType === 'wr') {
          const q0 = uniqueQuestions[0];
          addLog(`sample_solution: ${q0.sample_solution ? 'present' : 'NULL'}, sample_answer: ${q0.sample_answer ? 'present' : 'NULL'}, answer: ${q0.answer ? 'present' : 'NULL'}, solution: ${q0.solution ? 'present' : 'NULL'}`);
        }
      }

      const classSection = getClassSection();
      const stamped = uniqueQuestions.map(q => {
        // Gather ALL string values from the question to use as fallback
        const allValues = Object.values(q).filter(v => typeof v === 'string' && v.trim().length > 10);
        
        const questionText = q.question_text || q.question || q.prompt || q.text || 'No question text';
        const base = {
          chapter: selectedChapter,
          difficulty: difficulty,
          question_text: questionText,
          class_section: classSection
        };
        if (questionType === 'wr') {
          // WR table uses 'question' column, not 'question_text'
          base.question = questionText;
          // Try every possible key the LLM might use for the solution
          base.sample_solution = q.sample_solution || q.sample_answer || q.sampleSolution 
            || q.sampleAnswer || q.answer || q.solution || q.expected_answer 
            || q.model_answer || q.correct_answer || q.response
            || (allValues.length > 1 ? allValues[allValues.length - 1] : null)
            || 'No sample solution provided.';
        }
        if (questionType === 'mc') {
          base.explanation = q.explanation || 'N/A';
          base.correct_answer = q.correct_answer || q.correctAnswer || q.answer || 'A';
          base.option_a = q.option_a || q.optionA || q.A || '';
          base.option_b = q.option_b || q.optionB || q.B || '';
          base.option_c = q.option_c || q.optionC || q.C || '';
          base.option_d = q.option_d || q.optionD || q.D || '';
        }
        return base;
      });

      // Final safety check: remove any WR questions that STILL have no sample_solution
      if (questionType === 'wr') {
        for (const q of stamped) {
          if (!q.sample_solution || String(q.sample_solution).trim() === '' || q.sample_solution === 'null') {
            q.sample_solution = 'No sample solution provided.';
          } else {
            q.sample_solution = String(q.sample_solution);
          }
          // Also ensure question_text and question are strings
          q.question_text = String(q.question_text || 'No question text');
          q.question = String(q.question || q.question_text);
        }
        // Log all questions' sample_solution status
        addLog(`WR validation: ${stamped.map((q, i) => `Q${i + 1}: ${q.sample_solution ? q.sample_solution.substring(0, 30) + '...' : 'NULL!'}`).join(' | ')}`);
      }

      // For MC ensure all fields are strings
      if (questionType === 'mc') {
        for (const q of stamped) {
          q.question_text = String(q.question_text || 'No question text');
          q.option_a = String(q.option_a || '');
          q.option_b = String(q.option_b || '');
          q.option_c = String(q.option_c || '');
          q.option_d = String(q.option_d || '');
          q.correct_answer = String(q.correct_answer || 'A');
          q.explanation = String(q.explanation || 'N/A');
        }
      }

      // Step 8: Bulk insert via API — insert into each target section
      const sections = getTargetSections();
      addLog(`Inserting ${stamped.length} questions into ${sections.length} section(s)...`);
      const endpoint = questionType === 'mc' ? 'mcBank' : 'wrBank';
      let totalInserted = 0;
      for (const sec of sections) {
        const sectionStamped = stamped.map(q => ({ ...q, class_section: sec }));
        const insertResponse = await apiFetch(`${API_BASE_URL}/api/${endpoint}/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chapter: selectedChapter,
            class_section: sec,
            questions: sectionStamped
          })
        });

        if (!insertResponse.ok) {
          addLog(`⚠️ Insert failed for section ${sec}: ${insertResponse.status}`);
          continue;
        }

        const insertData = await insertResponse.json();
        totalInserted += insertData.inserted || 0;
        addLog(`  ✅ ${sec}: inserted ${insertData.inserted} questions`);
      }
      addLog(`✅ Total inserted: ${totalInserted} across ${sections.length} section(s)`);
      setGenerationResult({ inserted: totalInserted, duplicatesSkipped });

      // Refresh counts
      loadAllCounts(courseStructure);

    } catch (err) {
      addLog(`❌ Error: ${err.message}`);
      logger.error('Generation error:', err);
    }

    setGenerating(false);
  };

  // ==================== GENERATE SIMILAR QUESTIONS ====================
  const handleGenerateSimilar = async () => {
    if (!seedQuestion.trim()) return alert('Please paste a seed question first');
    if (!similarChapter) return alert('Please select a chapter');

    setSimilarGenerating(true);
    setSimilarResults([]);
    setSimilarLog('Fetching chapter content...');

    try {
      // Load chapter content for context
      let chapterContent = '';
      try {
        const res = await fetch(getCurriculumUrl(`${similarChapter}.txt`));
        if (res.ok) chapterContent = await res.text();
      } catch { /* ignore */ }

      const chapterTitle = (courseStructure.find(c => c.id === similarChapter) || {}).title || similarChapter;

      const systemPrompt = similarType === 'mc'
        ? `You are an expert Python programming instructor. Given a seed multiple-choice question, generate exactly 5 NEW multiple-choice questions that:
- Test the same concept in a different way (different scenario, values, wording, or angle)
- Match the difficulty: ${similarDifficulty}
- Are scoped to Chapter: ${similarChapter} — ${chapterTitle}
- Each must have exactly 4 options (A, B, C, D) and one correct answer
- Include a brief explanation for the correct answer

Return ONLY valid JSON: {"questions": [{"question_text":"...","option_a":"...","option_b":"...","option_c":"...","option_d":"...","correct_answer":"A","explanation":"...","difficulty":"${similarDifficulty}"}]}`
        : `You are an expert Python programming instructor. Given a seed written-response question, generate exactly 5 NEW written-response questions that:
- Test the same concept in a different way (different scenario, values, task, or angle)
- Match the difficulty: ${similarDifficulty}
- Are scoped to Chapter: ${similarChapter} — ${chapterTitle}
- Each must include a sample solution

Return ONLY valid JSON: {"questions": [{"question_text":"...","sample_solution":"...","difficulty":"${similarDifficulty}"}]}`;

      const userPrompt = `Seed question:\n${seedQuestion.trim()}\n\n${chapterContent ? `Chapter content for context:\n${chapterContent.substring(0, 8000)}` : ''}`;

      setSimilarLog('Asking GPT-4.1 to generate 5 similar questions...');

      const response = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'gpt-4.1',
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: userPrompt }
          ],
          temperature: 0.85,
          max_tokens: 8000,
          response_format: { type: 'json_object' }
        })
      });

      if (!response.ok) throw new Error(`API error: ${response.status}`);

      const data = await response.json();
      let raw = data.choices[0].message.content.trim();
      raw = raw.replace(/^```json\s*/i, '').replace(/^```\s*/i, '').replace(/\s*```$/i, '');

      const parsed = JSON.parse(raw);
      const questions = parsed.questions || parsed;
      if (!Array.isArray(questions) || questions.length === 0) throw new Error('No questions returned');

      setSimilarResults(questions.map((q, i) => ({ ...q, _id: i, _accepted: false, _skipped: false })));
      setSimilarLog(`✅ Generated ${questions.length} similar questions. Review and add to bank.`);
    } catch (err) {
      setSimilarLog(`❌ Error: ${err.message}`);
    }

    setSimilarGenerating(false);
  };

  const handleAddSimilarToBank = async (question) => {
    const classSection = getClassSection();
    const endpoint = similarType === 'mc' ? 'mcBank' : 'wrBank';
    const qText = question.question_text || question.question || '';
    const toInsert = similarType === 'mc'
      ? {
          chapter: similarChapter,
          difficulty: similarDifficulty,
          question_text: qText,
          class_section: classSection,
          option_a: String(question.option_a || ''),
          option_b: String(question.option_b || ''),
          option_c: String(question.option_c || ''),
          option_d: String(question.option_d || ''),
          correct_answer: String(question.correct_answer || 'A'),
          explanation: String(question.explanation || 'N/A'),
        }
      : {
          chapter: similarChapter,
          difficulty: similarDifficulty,
          question_text: qText,
          question: qText,
          sample_solution: String(question.sample_solution || question.sample_answer || 'N/A'),
          class_section: classSection,
        };

    const sections = getTargetSections();
    for (const sec of sections) {
      await apiFetch(`${API_BASE_URL}/api/${endpoint}/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chapter: similarChapter, class_section: sec, questions: [{ ...toInsert, class_section: sec }] })
      });
    }

    setSimilarResults(prev => prev.map(q => q._id === question._id ? { ...q, _accepted: true } : q));
    loadAllCounts(courseStructure);
  };

  // ==================== VIEW QUESTIONS ====================
  const handleViewQuestions = async (chapterId, type) => {
    setViewLoading(true);
    setViewQuestions([]);
    setViewChapter(chapterId);
    setViewType(type);
    setViewMode('view-chapter');

    try {
      const endpoint = type === 'mc' ? 'mcBank' : 'wrBank';
      const sections = getTargetSections();
      let allQuestions = [];
      for (const sec of sections) {
        const response = await apiFetch(`${API_BASE_URL}/api/${endpoint}/all?chapter=${chapterId}&class_section=${encodeURIComponent(sec)}`);
        if (response.ok) {
          const data = await response.json();
          const questions = (data.questions || []).map(q => ({ ...q, _section: sec }));
          allQuestions = [...allQuestions, ...questions];
        }
      }
      setViewQuestions(allQuestions);
    } catch (err) {
      logger.error('Error fetching questions:', err);
    }
    setViewLoading(false);
  };

  // ==================== DELETE ALL BY CHAPTER ====================
  const handleDeleteByChapter = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);

    try {
      const endpoint = deleteConfirm.type === 'mc' ? 'mcBank' : 'wrBank';
      const sections = getTargetSections();
      for (const sec of sections) {
        const response = await apiFetch(`${API_BASE_URL}/api/${endpoint}/delete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ chapter: deleteConfirm.chapter, class_section: sec })
        });
        if (response.ok) {
          addLog(`✅ Deleted ${deleteConfirm.type.toUpperCase()} for ${deleteConfirm.chapter} in ${sec}`);
        }
      }
      // Refresh counts and view
      loadAllCounts(courseStructure);
      if (viewMode && viewChapter === deleteConfirm.chapter && viewType === deleteConfirm.type) {
        setViewQuestions([]);
      }
    } catch (err) {
      addLog(`❌ Delete error: ${err.message}`);
    }

    setDeleteConfirm(null);
    setDeleting(false);
  };

  // ==================== DELETE SINGLE QUESTION ====================
  const handleDeleteSingle = async (questionId, type) => {
    setDeletingSingleId(questionId);
    try {
      const endpoint = type === 'mc' ? 'mcBank' : 'wrBank';
      const response = await apiFetch(`${API_BASE_URL}/api/${endpoint}/deleteSingle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: questionId })
      });

      if (response.ok) {
        // Remove from view
        setViewQuestions(prev => prev.filter(q => q.id !== questionId));
        // Refresh counts
        loadAllCounts(courseStructure);
      }
    } catch (err) {
      logger.error('Error deleting question:', err);
    }
    setDeletingSingleId(null);
  };

  // ==================== EDIT / UPDATE SINGLE QUESTION ====================
  const handleEditSave = async () => {
    if (!editingQuestion) return;
    setSavingEdit(true);
    try {
      const type = viewType;
      const endpoint = type === 'mc' ? 'mcBank' : 'wrBank';
      const payload = { id: editingQuestion.id };

      if (type === 'mc') {
        payload.question_text = editingQuestion.question_text || editingQuestion.question;
        payload.option_a = editingQuestion.option_a;
        payload.option_b = editingQuestion.option_b;
        payload.option_c = editingQuestion.option_c;
        payload.option_d = editingQuestion.option_d;
        payload.correct_answer = editingQuestion.correct_answer;
        payload.explanation = editingQuestion.explanation || '';
        payload.difficulty = editingQuestion.difficulty;
      } else {
        payload.question_text = editingQuestion.question_text || editingQuestion.question;
        payload.sample_solution = editingQuestion.sample_solution || editingQuestion.sample_answer || '';
        payload.difficulty = editingQuestion.difficulty;
      }

      const response = await apiFetch(`${API_BASE_URL}/api/${endpoint}/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        // Update in local view list
        setViewQuestions(prev => prev.map(q =>
          (q.id === editingQuestion.id && (q._section || '') === (editingQuestion._section || ''))
            ? { ...q, ...payload, question: payload.question_text }
            : q
        ));
        setEditingQuestion(null);
      } else {
        alert('Failed to save question. Check backend logs.');
      }
    } catch (err) {
      logger.error('Error updating question:', err);
      alert('Error updating question: ' + err.message);
    }
    setSavingEdit(false);
  };

  // ==================== STYLES ==
  const styles = {
    outerContainer: { overflowY: 'auto', height: '100%', color: '#c9d1d9' },
    container: { padding: '24px', maxWidth: '900px', margin: '0 auto' },
    title: { fontSize: '22px', fontWeight: '700', color: '#aff5b4', marginBottom: '20px' },
    card: {
      backgroundColor: '#161b22', border: '1px solid rgba(254,240,138,0.3)', borderRadius: '10px',
      padding: '20px', marginBottom: '20px'
    },
    label: { fontSize: '13px', color: '#aff5b4', marginBottom: '0', display: 'block', fontWeight: '600', minWidth: '130px', lineHeight: '38px' },
    formRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' },
    formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', marginBottom: '12px' },
    formField: { display: 'flex', alignItems: 'center', gap: '12px' },
    select: {
      flex: 1, maxWidth: '300px', padding: '8px 12px', backgroundColor: '#0d1117', color: '#c9d1d9',
      border: '1px solid rgba(254,240,138,0.5)', borderRadius: '6px', fontSize: '14px'
    },
    input: {
      width: '100px', padding: '8px 12px', backgroundColor: '#0d1117', color: '#c9d1d9',
      border: '1px solid rgba(254,240,138,0.5)', borderRadius: '6px', fontSize: '14px'
    },
    radioGroup: { display: 'flex', gap: '16px' },
    radioLabel: { display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px', color: '#c9d1d9' },
    generateBtn: {
      padding: '12px 28px', backgroundColor: '#aff5b4', color: '#000', border: 'none',
      borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer',
      opacity: generating ? 0.6 : 1
    },
    logBox: {
      backgroundColor: '#0d1117', border: '1px solid #30363d', borderRadius: '8px',
      padding: '12px', maxHeight: '200px', overflowY: 'auto', fontFamily: 'monospace',
      fontSize: '12px', color: '#8b949e', marginTop: '12px'
    },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
    th: {
      textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #30363d',
      color: '#f5e6a3', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase'
    },
    td: { padding: '10px 12px', borderBottom: '1px solid #21262d', verticalAlign: 'top', textAlign: 'left' },
    badge: (color) => ({
      display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '11px',
      fontWeight: '600', backgroundColor: color + '20', color: color, border: `1px solid ${color}40`
    }),
    smallBtn: (color) => ({
      padding: '4px 10px', backgroundColor: 'transparent', color: color,
      border: `1px solid ${color}`, borderRadius: '4px', fontSize: '11px', cursor: 'pointer'
    }),
    chapterRow: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 16px', border: '1px solid rgba(254,240,138,0.3)', borderRadius: '8px',
      marginBottom: '6px', backgroundColor: '#0d1117'
    },
    countBadge: {
      display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px',
      fontWeight: '600', marginLeft: '6px'
    }
  };

  const difficultyColor = {
    easy: '#3fb950',
    medium: '#d29922',
    hard: '#f85149',
    'very hard': '#bc4c00'
  };

  // ==================== RENDER ====================

  // If viewing questions
  if (viewMode === 'view-chapter') {
    return (
      <div style={styles.outerContainer}>
      <div style={styles.container}>
        <button
          onClick={() => { setViewMode(null); setViewQuestions([]); }}
          style={{ ...styles.smallBtn('#58a6ff'), marginBottom: '16px', fontSize: '13px', padding: '8px 16px' }}
        >
          ← Back to Question Bank
        </button>

        <h2 style={{ ...styles.title, marginBottom: '8px' }}>
          {viewType.toUpperCase()} Questions — {viewChapter}
        </h2>
        <p style={{ color: '#8b949e', fontSize: '13px', marginBottom: '16px' }}>
          {viewQuestions.length} question{viewQuestions.length !== 1 ? 's' : ''} found
        </p>

        {viewLoading ? (
          <p style={{ color: '#8b949e' }}>Loading questions...</p>
        ) : viewQuestions.length === 0 ? (
          <p style={{ color: '#8b949e' }}>No questions found for this chapter.</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: '40px' }}>ID</th>
                {qbSection === 'all' && <th style={{ ...styles.th, width: '120px' }}>Section</th>}
                <th style={{ ...styles.th, width: '80px' }}>Difficulty</th>
                <th style={styles.th}>Question</th>
                {viewType === 'mc' && <th style={{ ...styles.th, width: '60px' }}>Answer</th>}
                <th style={{ ...styles.th, width: '100px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {viewQuestions.map((q, idx) => (
                <tr key={`${q.id}-${q._section || idx}`} style={{ backgroundColor: idx % 2 === 0 ? 'transparent' : '#161b2240' }}>
                  <td style={styles.td}>{q.id}</td>
                  {qbSection === 'all' && (
                    <td style={styles.td}>
                      <span style={{ fontSize: '11px', color: '#f0d060', fontWeight: '600' }}>{q._section || ''}</span>
                    </td>
                  )}
                  <td style={styles.td}>
                    <span style={styles.badge(difficultyColor[q.difficulty] || '#8b949e')}>
                      {q.difficulty}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {editingQuestion && editingQuestion.id === q.id && (editingQuestion._section || '') === (q._section || '') ? (
                      /* ---- EDIT MODE ---- */
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <label style={{ fontSize: '11px', color: '#aff5b4' }}>Question:</label>
                        <textarea
                          value={editingQuestion.question_text || editingQuestion.question || ''}
                          onChange={e => setEditingQuestion({ ...editingQuestion, question_text: e.target.value, question: e.target.value })}
                          rows={4}
                          style={{ width: '100%', padding: '6px', backgroundColor: '#0d1117', color: '#c9d1d9', border: '1px solid rgba(254,240,138,0.5)', borderRadius: '4px', fontFamily: 'monospace', fontSize: '13px', resize: 'vertical' }}
                        />
                        {viewType === 'mc' && (
                          <>
                            {['option_a','option_b','option_c','option_d'].map(opt => (
                              <div key={opt} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ fontSize: '12px', color: '#8b949e', minWidth: '20px' }}>{opt.slice(-1).toUpperCase()})</span>
                                <input
                                  value={editingQuestion[opt] || ''}
                                  onChange={e => setEditingQuestion({ ...editingQuestion, [opt]: e.target.value })}
                                  style={{ flex: 1, padding: '4px 6px', backgroundColor: '#0d1117', color: '#c9d1d9', border: '1px solid rgba(254,240,138,0.4)', borderRadius: '4px', fontFamily: 'monospace', fontSize: '12px' }}
                                />
                              </div>
                            ))}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontSize: '12px', color: '#8b949e', minWidth: '60px' }}>Answer:</span>
                              <select
                                value={editingQuestion.correct_answer || ''}
                                onChange={e => setEditingQuestion({ ...editingQuestion, correct_answer: e.target.value })}
                                style={{ padding: '4px 8px', backgroundColor: '#0d1117', color: '#58a6ff', border: '1px solid rgba(254,240,138,0.4)', borderRadius: '4px', fontSize: '13px', fontWeight: '700' }}
                              >
                                <option value="A">A</option>
                                <option value="B">B</option>
                                <option value="C">C</option>
                                <option value="D">D</option>
                              </select>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                              <span style={{ fontSize: '12px', color: '#8b949e', minWidth: '60px', paddingTop: '4px' }}>Explain:</span>
                              <textarea
                                value={editingQuestion.explanation || ''}
                                onChange={e => setEditingQuestion({ ...editingQuestion, explanation: e.target.value })}
                                rows={2}
                                style={{ flex: 1, padding: '4px 6px', backgroundColor: '#0d1117', color: '#3fb950', border: '1px solid rgba(254,240,138,0.4)', borderRadius: '4px', fontFamily: 'monospace', fontSize: '12px', resize: 'vertical' }}
                              />
                            </div>
                          </>
                        )}
                        {viewType === 'wr' && (
                          <>
                            <label style={{ fontSize: '11px', color: '#aff5b4' }}>Sample Solution:</label>
                            <textarea
                              value={editingQuestion.sample_solution || editingQuestion.sample_answer || ''}
                              onChange={e => setEditingQuestion({ ...editingQuestion, sample_solution: e.target.value, sample_answer: e.target.value })}
                              rows={4}
                              style={{ width: '100%', padding: '6px', backgroundColor: '#0d1117', color: '#3fb950', border: '1px solid rgba(254,240,138,0.5)', borderRadius: '4px', fontFamily: 'monospace', fontSize: '13px', resize: 'vertical' }}
                            />
                          </>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '12px', color: '#8b949e', minWidth: '60px' }}>Difficulty:</span>
                          <select
                            value={editingQuestion.difficulty || 'medium'}
                            onChange={e => setEditingQuestion({ ...editingQuestion, difficulty: e.target.value })}
                            style={{ padding: '4px 8px', backgroundColor: '#0d1117', color: '#c9d1d9', border: '1px solid rgba(254,240,138,0.4)', borderRadius: '4px', fontSize: '12px' }}
                          >
                            <option value="easy">Easy</option>
                            <option value="medium">Medium</option>
                            <option value="hard">Hard</option>
                          </select>
                        </div>
                      </div>
                    ) : (
                      /* ---- READ-ONLY MODE ---- */
                      <>
                        <div style={{ marginBottom: '6px', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{renderFormatted(q.question_text || q.question)}</div>
                        {viewType === 'mc' && (
                          <div style={{ fontSize: '12px', color: '#8b949e', lineHeight: '1.6', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                            <div>A) {renderFormatted(q.option_a)}</div>
                            <div>B) {renderFormatted(q.option_b)}</div>
                            <div>C) {renderFormatted(q.option_c)}</div>
                            <div>D) {renderFormatted(q.option_d)}</div>
                            {q.explanation && (
                              <div style={{ marginTop: '4px', color: '#3fb950', fontStyle: 'italic', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                                💡 {renderFormatted(q.explanation)}
                              </div>
                            )}
                          </div>
                        )}
                        {viewType === 'wr' && (q.sample_solution || q.sample_answer) && (
                          <div style={{ fontSize: '12px', color: '#3fb950', marginTop: '4px', fontStyle: 'italic', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                            📝 Sample: {renderFormatted((q.sample_solution || q.sample_answer || '').substring(0, 200))}{(q.sample_solution || q.sample_answer || '').length > 200 ? '...' : ''}
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  {viewType === 'mc' && (
                    <td style={{ ...styles.td, fontWeight: '700', color: '#58a6ff', fontSize: '16px' }}>
                      {editingQuestion && editingQuestion.id === q.id ? editingQuestion.correct_answer : q.correct_answer}
                    </td>
                  )}
                  <td style={styles.td}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                      {editingQuestion && editingQuestion.id === q.id && (editingQuestion._section || '') === (q._section || '') ? (
                        /* Save / Cancel buttons */
                        <>
                          <button
                            onClick={handleEditSave}
                            disabled={savingEdit}
                            style={{ ...styles.smallBtn('#3fb950'), opacity: savingEdit ? 0.5 : 1, fontSize: '12px', padding: '4px 10px' }}
                          >
                            {savingEdit ? '...' : '💾'}
                          </button>
                          <button
                            onClick={() => setEditingQuestion(null)}
                            style={{ ...styles.smallBtn('#8b949e'), fontSize: '12px', padding: '4px 10px' }}
                          >
                            ✖
                          </button>
                        </>
                      ) : (
                        /* Edit / Delete buttons */
                        <>
                          <button
                            onClick={() => setEditingQuestion({ ...q })}
                            disabled={!!editingQuestion}
                            style={{ ...styles.smallBtn('#58a6ff'), opacity: editingQuestion ? 0.5 : 1, fontSize: '12px', padding: '4px 10px' }}
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDeleteSingle(q.id, viewType)}
                            disabled={deletingSingleId === q.id || !!editingQuestion}
                            style={{
                              ...styles.smallBtn('#f85149'),
                              opacity: (deletingSingleId === q.id || editingQuestion) ? 0.5 : 1,
                              fontSize: '12px', padding: '4px 10px'
                            }}
                          >
                            {deletingSingleId === q.id ? '...' : '🗑️'}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </div>
    );
  }

  // Main generator view
  return (
    <div style={styles.outerContainer}>
    <div style={styles.container}>
      <h2 style={styles.title}>📦 Question Bank Generator</h2>

      {/* Section Selector */}
      {allUserSections.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px',
          padding: '10px 16px', backgroundColor: '#161b22', border: '1px solid rgba(254,240,138,0.3)',
          borderRadius: '8px' }}>
          <span style={{ color: '#f0d060', fontSize: '13px', fontWeight: '600' }}>📌 Section:</span>
          <select
            value={qbSection}
            onChange={(e) => setQbSection(e.target.value)}
            style={{
              backgroundColor: '#0d1117', color: '#fff5b0',
              border: '1px solid rgba(254,240,138,0.4)', borderRadius: '6px',
              padding: '6px 12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
            }}
          >
            <option value="all">All Sections</option>
            {allUserSections.map(sec => (
              <option key={sec} value={sec}>{sec}-{university}</option>
            ))}
          </select>
          <span style={{ color: '#8b949e', fontSize: '11px' }}>
            {qbSection === 'all' ? '⚡ Generate/delete will apply to all sections' : `Scoped to ${qbSection}-${university}`}
          </span>
        </div>
      )}

      {/* Generator Form */}
      <div style={styles.card}>
        <h3 style={{ color: '#58a6ff', fontSize: '16px', marginBottom: '16px' }}>Generate New Questions</h3>

        <div style={styles.formGrid}>
          {/* Chapter Select */}
          <div style={styles.formField}>
            <label style={styles.label}>Chapter</label>
            <select
              value={selectedChapter}
              onChange={(e) => setSelectedChapter(e.target.value)}
              style={styles.select}
              disabled={generating}
            >
              <option value="">-- Select Chapter --</option>
              {courseStructure.map(ch => (
                <option key={ch.id} value={ch.id}>
                  {ch.id} — {ch.title}
                </option>
              ))}
            </select>
          </div>

          {/* Difficulty Select */}
          <div style={styles.formField}>
            <label style={styles.label}>Difficulty</label>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              style={styles.select}
              disabled={generating}
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
              <option value="very hard">Very Hard</option>
            </select>
          </div>

          {/* Question Type */}
          <div style={styles.formField}>
            <label style={styles.label}>Question Type</label>
            <div style={styles.radioGroup}>
              <label style={styles.radioLabel}>
                <input
                  type="radio" name="qtype" value="mc"
                  checked={questionType === 'mc'}
                  onChange={() => setQuestionType('mc')}
                  disabled={generating}
                />
                MC
              </label>
              <label style={styles.radioLabel}>
                <input
                  type="radio" name="qtype" value="wr"
                  checked={questionType === 'wr'}
                  onChange={() => setQuestionType('wr')}
                  disabled={generating}
                />
                WR
              </label>
            </div>
          </div>

          {/* Question Count */}
          <div style={styles.formField}>
            <label style={styles.label}>Count</label>
            <input
              type="number" min="1" max="100"
              value={questionCount}
              onChange={(e) => setQuestionCount(parseInt(e.target.value) || 1)}
              style={styles.input}
              disabled={generating}
            />
            <div style={{ fontSize: '9px', color: '#fef08a', marginTop: '4px', lineHeight: '1.4' }}>
              WR: more than 10 can be very slow · MC: keep under 30
            </div>
          </div>
        </div>

        <div style={{ marginTop: '16px' }}>
          <button
            onClick={handleGenerate}
            disabled={generating || !selectedChapter}
            style={styles.generateBtn}
          >
            {generating ? '⏳ Generating...' : '🚀 Generate Questions'}
          </button>
        </div>

        {/* Generation Log */}
        {generationLog.length > 0 && (
          <div style={styles.logBox}>
            {generationLog.map((log, i) => (
              <div key={i}>{log}</div>
            ))}
          </div>
        )}

        {/* Generation Result */}
        {generationResult && (
          <div style={{
            marginTop: '12px', padding: '12px 16px', borderRadius: '8px',
            backgroundColor: generationResult.inserted > 0 ? '#238636' + '20' : '#f8514920',
            border: `1px solid ${generationResult.inserted > 0 ? '#238636' : '#f85149'}`,
            fontSize: '14px'
          }}>
            {generationResult.inserted > 0 ? '✅' : '⚠️'} Inserted: <strong>{generationResult.inserted}</strong>
            {generationResult.duplicatesSkipped > 0 && (
              <span> | Duplicates skipped: <strong>{generationResult.duplicatesSkipped}</strong></span>
            )}
          </div>
        )}
      </div>

      {/* Generate Similar Questions */}
      <div style={styles.card}>
        <h3 style={{ color: '#f0ad4e', fontSize: '16px', marginBottom: '4px' }}>🔁 Generate Similar Questions</h3>
        <p style={{ color: '#8b949e', fontSize: '12px', marginBottom: '16px' }}>Paste a question and generate 5 variations to add to the bank.</p>

        {/* Seed Question */}
        <div style={{ marginBottom: '12px' }}>
          <label style={styles.label}>Seed Question (paste the full question + options if MC)</label>
          <textarea
            value={seedQuestion}
            onChange={e => setSeedQuestion(e.target.value)}
            rows={5}
            placeholder={`e.g.\nWhat is the output of: print(2 ** 3)\nA) 6  B) 8  C) 9  D) 5\nAnswer: B`}
            style={{ ...styles.input, width: '100%', resize: 'vertical', fontFamily: 'monospace', fontSize: '13px', boxSizing: 'border-box' }}
            disabled={similarGenerating}
          />
        </div>

        <div style={styles.formGrid}>
          {/* Chapter */}
          <div style={styles.formField}>
            <label style={styles.label}>Chapter / Slide</label>
            <select value={similarChapter} onChange={e => setSimilarChapter(e.target.value)} style={styles.select} disabled={similarGenerating}>
              <option value="">-- Select --</option>
              {courseStructure.map(ch => (
                <option key={ch.id} value={ch.id}>{ch.id} — {ch.title}</option>
              ))}
            </select>
          </div>
          {/* Difficulty */}
          <div style={styles.formField}>
            <label style={styles.label}>Difficulty</label>
            <select value={similarDifficulty} onChange={e => setSimilarDifficulty(e.target.value)} style={styles.select} disabled={similarGenerating}>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
              <option value="very hard">Very Hard</option>
            </select>
          </div>
          {/* Type */}
          <div style={styles.formField}>
            <label style={styles.label}>Question Type</label>
            <div style={styles.radioGroup}>
              <label style={styles.radioLabel}><input type="radio" value="mc" checked={similarType === 'mc'} onChange={() => setSimilarType('mc')} disabled={similarGenerating} /> MC</label>
              <label style={styles.radioLabel}><input type="radio" value="wr" checked={similarType === 'wr'} onChange={() => setSimilarType('wr')} disabled={similarGenerating} /> WR</label>
            </div>
          </div>
        </div>

        <button
          onClick={handleGenerateSimilar}
          disabled={similarGenerating || !seedQuestion.trim() || !similarChapter}
          style={{ ...styles.generateBtn, backgroundColor: '#9a5c00', marginTop: '12px' }}
        >
          {similarGenerating ? '⏳ Generating...' : '🔁 Generate 5 Similar Questions'}
        </button>

        {similarLog && (
          <div style={{ marginTop: '10px', fontSize: '12px', color: similarLog.startsWith('❌') ? '#f85149' : '#3fb950', fontFamily: 'monospace' }}>
            {similarLog}
          </div>
        )}

        {/* Result Cards */}
        {similarResults.length > 0 && (
          <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {similarResults.map((q) => (
              !q._skipped && (
                <div key={q._id} style={{
                  backgroundColor: q._accepted ? '#1a3a1a' : '#161b22',
                  border: `1px solid ${q._accepted ? '#3fb950' : 'rgba(254,240,138,0.2)'}`,
                  borderRadius: '8px', padding: '14px 16px'
                }}>
                  <div style={{ fontSize: '14px', color: '#c9d1d9', marginBottom: '10px', lineHeight: 1.6 }}>
                    <strong style={{ color: '#f0d060' }}>Q{q._id + 1}:</strong> {q.question_text || q.question}
                  </div>
                  {similarType === 'mc' && (
                    <div style={{ fontSize: '12px', color: '#8b949e', marginBottom: '8px', fontFamily: 'monospace' }}>
                      A) {q.option_a} &nbsp; B) {q.option_b} &nbsp; C) {q.option_c} &nbsp; D) {q.option_d}<br/>
                      <span style={{ color: '#3fb950' }}>✓ {q.correct_answer}</span>
                      {q.explanation && <span style={{ color: '#8b949e' }}> — {q.explanation}</span>}
                    </div>
                  )}
                  {similarType === 'wr' && q.sample_solution && (
                    <div style={{ fontSize: '12px', color: '#8b949e', marginBottom: '8px', fontFamily: 'monospace' }}>
                      <span style={{ color: '#3fb950' }}>Solution:</span> {q.sample_solution}
                    </div>
                  )}
                  {!q._accepted ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleAddSimilarToBank(q)}
                        style={{ ...styles.smallBtn('#3fb950'), fontSize: '12px', padding: '5px 14px' }}
                      >
                        ✅ Add to Bank
                      </button>
                      <button
                        onClick={() => setSimilarResults(prev => prev.map(r => r._id === q._id ? { ...r, _skipped: true } : r))}
                        style={{ ...styles.smallBtn('#8b949e'), fontSize: '12px', padding: '5px 14px' }}
                      >
                        ✖ Skip
                      </button>
                    </div>
                  ) : (
                    <span style={{ color: '#3fb950', fontSize: '12px', fontWeight: '700' }}>✅ Added to bank</span>
                  )}
                </div>
              )
            ))}
          </div>
        )}
      </div>

      {/* Question Bank Overview */}
      <div style={styles.card}>
        <h3 style={{ color: '#58a6ff', fontSize: '16px', marginBottom: '16px', textAlign: 'center' }}>
          📋 Question Bank Overview
          {countsLoading && <span style={{ fontSize: '12px', color: '#8b949e', marginLeft: '8px' }}>Loading...</span>}
        </h3>

        {structureLoading ? (
          <p style={{ color: '#8b949e' }}>Loading chapters...</p>
        ) : (
          <div>
            {courseStructure.map(ch => {
              const counts = bankCounts[ch.id] || { mc_count: 0, wr_count: 0 };
              return (
                <div key={ch.id} style={styles.chapterRow}>
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div style={{ marginBottom: '4px' }}>
                      <span style={{ fontWeight: '600', color: '#c9d1d9' }}>{ch.id}</span>
                      <span style={{ color: '#8b949e', marginLeft: '8px', fontSize: '13px' }}>{ch.title}</span>
                    </div>
                    <div>
                      <span style={{
                        ...styles.countBadge,
                        marginLeft: 0,
                        backgroundColor: counts.mc_count > 0 ? '#23863620' : '#21262d',
                        color: counts.mc_count > 0 ? '#3fb950' : '#8b949e'
                      }}>
                        MC: {counts.mc_count}
                      </span>
                      <span style={{
                        ...styles.countBadge,
                        backgroundColor: counts.wr_count > 0 ? '#388bfd20' : '#21262d',
                        color: counts.wr_count > 0 ? '#58a6ff' : '#8b949e'
                      }}>
                        WR: {counts.wr_count}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '6px' }}>
                    {counts.mc_count > 0 && (
                      <>
                        <button onClick={() => handleViewQuestions(ch.id, 'mc')} style={styles.smallBtn('#3fb950')}>
                          View MC
                        </button>
                        <button onClick={() => setDeleteConfirm({ chapter: ch.id, type: 'mc' })} style={styles.smallBtn('#ff7b72')}>
                          Delete MC
                        </button>
                      </>
                    )}
                    {counts.wr_count > 0 && (
                      <>
                        <button onClick={() => handleViewQuestions(ch.id, 'wr')} style={styles.smallBtn('#58a6ff')}>
                          View WR
                        </button>
                        <button onClick={() => setDeleteConfirm({ chapter: ch.id, type: 'wr' })} style={styles.smallBtn('#ff7b72')}>
                          Delete WR
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 10000
        }}>
          <div style={{
            backgroundColor: '#161b22', border: '1px solid #f85149', borderRadius: '12px',
            padding: '24px', maxWidth: '400px', textAlign: 'center'
          }}>
            <p style={{ fontSize: '16px', color: '#f85149', fontWeight: '700', marginBottom: '12px' }}>
              ⚠️ Confirm Delete
            </p>
            <p style={{ color: '#c9d1d9', fontSize: '14px', marginBottom: '20px' }}>
              Delete ALL <strong>{deleteConfirm.type.toUpperCase()}</strong> questions for{' '}
              <strong>{deleteConfirm.chapter}</strong>?<br />
              This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{ ...styles.smallBtn('#8b949e'), padding: '8px 20px', fontSize: '13px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteByChapter}
                disabled={deleting}
                style={{
                  padding: '8px 20px', backgroundColor: '#f85149', color: '#fff',
                  border: 'none', borderRadius: '4px', fontSize: '13px', cursor: 'pointer',
                  opacity: deleting ? 0.6 : 1
                }}
              >
                {deleting ? 'Deleting...' : 'Yes, Delete All'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default QuestionBankAdmin;
